/**
 * InfrastructureMapCanvas component
 *
 * Main container orchestrating the infrastructure map view, including:
 * - MapToolbar for style selection and controls
 * - SVG canvas with pan/zoom for node and link rendering
 * - OrphanPanel for unlinked endpoints
 * - LinkCreationTool for creating relationships
 *
 * Interaction hooks (pan/zoom, selection, drag, handlers, context menus,
 * derived data) are bundled in `use-map-canvas-orchestrator.ts`.
 * Heavy layout calculations live in `infrastructureMapCanvas.helpers.ts`;
 * context menus and detail modals live in `./canvas/*`.
 */

import { ReactElement } from 'react'
import type { InfrastructureMapCanvasProps } from '../../types/infrastructure-map'
import { useInfrastructureMap } from '../../hooks/use-infrastructure-map'
import { useCollectionSet, useEndpoints, useRelationships } from '../../hooks'
import {
  useContainerResizeObserver,
  useInitialMapStyle,
  useAutoCalculateLayout,
} from './infrastructure-map-canvas.hooks'
import { useCanvasState, useMapDeps } from './infrastructure-map-canvas.state'
import { useMapCanvasOrchestrator } from './use-map-canvas-orchestrator'
import { CanvasToolbar, MapCanvasArea, MapCanvasOverlays } from './infrastructure-map-canvas.view'

function CanvasLoading(): ReactElement {
  return (
    <div className="flex h-full items-center justify-center bg-slate-50 dark:bg-slate-900">
      <div className="text-slate-500 dark:text-slate-400">Loading infrastructure map...</div>
    </div>
  )
}

export function InfrastructureMapCanvas({
  caseId,
  initialStyle = 'tree',
  mapSet: externalMapSet,
}: InfrastructureMapCanvasProps): ReactElement {
  const { endpoints, loading: endpointsLoading } = useEndpoints(caseId)
  const { relationships, loading: relationshipsLoading } = useRelationships(caseId)
  const state = useCanvasState()
  const { linkedEndpointIds, endpointIds, parentChildMap } = useMapDeps(endpoints, relationships)
  const infraMap = useInfrastructureMap({
    caseId,
    endpointIds,
    linkedEndpointIds,
    parentChildMap,
    ...(externalMapSet ? { externalMapSet } : {}),
  })
  const collection = useCollectionSet()

  useContainerResizeObserver(state.containerRef, state.setViewport)
  useInitialMapStyle(initialStyle, infraMap)
  useAutoCalculateLayout(infraMap)

  const orchestrator = useMapCanvasOrchestrator({
    state,
    infraMap,
    collection,
    endpoints,
    relationships,
    parentChildMap,
  })

  if (endpointsLoading || relationshipsLoading) return <CanvasLoading />

  const detailsEndpoint = state.detailsEndpointId
    ? (orchestrator.derived.getEndpointById(state.detailsEndpointId) ?? null)
    : null
  const editingRelationship = state.editLinkId
    ? relationships.find((rel) => rel.id === state.editLinkId)
    : null

  return (
    <div className="flex h-full flex-col bg-white dark:bg-slate-900">
      <CanvasToolbar infraMap={infraMap} panZoom={orchestrator.panZoom} />
      <MapCanvasArea
        containerRef={state.containerRef}
        canvasRef={state.canvasRef}
        isPanning={state.isPanning}
        viewport={state.viewport}
        panZoom={orchestrator.panZoom}
        selection={orchestrator.selection}
        dragHandlers={orchestrator.dragHandlers}
        infraMap={infraMap}
        derived={orchestrator.derived}
        selectedEndpointIds={state.selectedEndpointIds}
        taggedEndpointIds={collection.taggedEndpointIds}
        editLinkId={state.editLinkId}
        handlers={orchestrator.handlers}
      />
      <MapCanvasOverlays
        linkCreation={infraMap.linkCreation}
        nodeContextMenu={state.nodeContextMenu}
        contextActions={orchestrator.contextActions}
        detailsEndpoint={detailsEndpoint}
        setDetailsEndpointId={state.setDetailsEndpointId}
        linkContextMenu={state.linkContextMenu}
        editingRelationship={editingRelationship}
        getEndpointById={orchestrator.derived.getEndpointById}
        pendingExpandConfirmation={infraMap.pendingExpandConfirmation}
        confirmRecursiveAction={infraMap.confirmRecursiveAction}
        cancelRecursiveAction={infraMap.cancelRecursiveAction}
        handleViewLinkDetails={orchestrator.handlers.handleViewLinkDetails}
        handleCloseLinkModal={orchestrator.handlers.handleCloseLinkModal}
        handleLinkComplete={orchestrator.handlers.handleLinkComplete}
      />
    </div>
  )
}

export default InfrastructureMapCanvas
